import { intlLocaleOf, type Locale, type MessageId } from "@/shared/i18n/messages";

export const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
/** The longest a single entry (or one timesheet cell) may be: 24 hours. */
export const MAX_ENTRY_MINUTES = 24 * MINUTES_PER_HOUR;

type Translate = (id: MessageId, values?: Record<string, string | number>) => string;

const ARABIC_INDIC_ZERO = 0x0660;
const EXTENDED_ARABIC_INDIC_ZERO = 0x06f0;

/** Arabic keyboards type ٠١٢… and the Arabic decimal sign ٫; read them as 0, 1, 2… and ".". */
export function latinDigits(text: string): string {
  return text
    .replace(/[\u0660-\u0669\u06f0-\u06f9]/g, (digit) => {
      const code = digit.charCodeAt(0);
      const zero =
        code >= EXTENDED_ARABIC_INDIC_ZERO ? EXTENDED_ARABIC_INDIC_ZERO : ARABIC_INDIC_ZERO;
      return String(code - zero);
    })
    .replace(/[\u066b,]/g, ".");
}

const CLOCK = /^(\d{1,2}):(\d{1,2})$/;
const WHOLE = /^\d+$/;
const DECIMAL = /^\d*\.\d+$/;
/* "2h 15m", "2h", "45m", "1.5h", "2 س 15 د", "2 ساعة", "١٥ دقيقة". */
const UNITS =
  /^(?:(\d*\.?\d+)\s*(?:h|hr|hrs|hour|hours|س|ساعة|ساعات)\s*)?(?:(\d+)\s*(?:m|min|mins|minute|minutes|د|دقيقة|دقائق)?)?$/i;

/**
 * Minutes from what someone types into a duration box. A bare whole number is minutes ("45"),
 * a decimal is hours ("1.5"), and "h:mm" is hours and minutes ("2:15"); units work too ("2h 15m",
 * "٢ س ١٥ د"). Blank is 0. Anything unreadable gives null.
 */
export function parseDuration(raw: string): number | null {
  const text = latinDigits(raw).trim().replace(/\s+/g, " ");
  if (text === "") return 0;
  const clock = CLOCK.exec(text);
  if (clock) {
    const minutes = Number(clock[2]);
    return minutes < MINUTES_PER_HOUR ? Number(clock[1]) * MINUTES_PER_HOUR + minutes : null;
  }
  if (WHOLE.test(text)) return Number(text);
  if (DECIMAL.test(text)) return Math.round(Number(text) * MINUTES_PER_HOUR);
  const units = UNITS.exec(text);
  if (!units || (units[1] === undefined && units[2] === undefined)) return null;
  const hours = units[1] === undefined ? 0 : Number(units[1]);
  const minutes = units[2] === undefined ? 0 : Number(units[2]);
  return Math.round(hours * MINUTES_PER_HOUR + minutes);
}

/** Whether a typed duration is one an entry may have (1 minute to 24 hours, or 0 to clear). */
export function isLoggableMinutes(minutes: number | null, { allowZero = false } = {}): boolean {
  if (minutes === null || !Number.isInteger(minutes)) return false;
  return (allowZero && minutes === 0) || (minutes >= 1 && minutes <= MAX_ENTRY_MINUTES);
}

export function splitMinutes(total: number): { hours: number; minutes: number } {
  const whole = Math.max(0, Math.round(total));
  return { hours: Math.floor(whole / MINUTES_PER_HOUR), minutes: whole % MINUTES_PER_HOUR };
}

/** "2h 15m", "2h", "45m" / "٢ س ١٥ د": how every duration is written in the app. */
export function formatDuration(total: number, t: Translate): string {
  const { hours, minutes } = splitMinutes(total);
  if (hours === 0) return t("time.duration.minutes", { minutes });
  if (minutes === 0) return t("time.duration.hours", { hours });
  return t("time.duration.hoursMinutes", { hours, minutes });
}

/** "7:30" / "٧:٣٠": the compact form in timesheet cells; empty for no time. */
export function formatHoursMinutes(total: number, locale: Locale): string {
  if (total <= 0) return "";
  const { hours, minutes } = splitMinutes(total);
  const tag = intlLocaleOf(locale);
  const hourText = new Intl.NumberFormat(tag).format(hours);
  const minuteText = new Intl.NumberFormat(tag, { minimumIntegerDigits: 2 }).format(minutes);
  return `${hourText}:${minuteText}`;
}

/** "1:05:23" / "١:٠٥:٢٣": a running timer's clock, hours unpadded. */
export function formatClock(totalSeconds: number, locale: Locale): string {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  const tag = intlLocaleOf(locale);
  const two = new Intl.NumberFormat(tag, { minimumIntegerDigits: 2 });
  const hours = Math.floor(seconds / (SECONDS_PER_MINUTE * MINUTES_PER_HOUR));
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE) % MINUTES_PER_HOUR;
  return `${new Intl.NumberFormat(tag).format(hours)}:${two.format(minutes)}:${two.format(seconds % SECONDS_PER_MINUTE)}`;
}

/** Seconds a timer has run, from its start to now. */
export function elapsedSeconds(startedAt: string, now: number): number {
  return Math.max(0, Math.floor((now - new Date(startedAt).getTime()) / 1000));
}
